import { useRef, useCallback } from 'react';
import * as Cesium from 'cesium';

/**
 * Hook for managing Cesium scene rendering settings.
 * Handles point size, EDL, globe/atmosphere/terrain, SSAO, FXAA, contour lines.
 * @param {React.RefObject} viewerRef - Reference to the Cesium viewer
 * @param {React.RefObject} tilesetRef - Reference to the point cloud tileset
 */
export const useSceneSettings = (viewerRef, tilesetRef) => {
  // EDL settings refs — updated by updateEyeDomeLighting* so loadTileset always reads
  // the saved values and never initializes with stale defaults that immediately get overridden
  const edlStrengthRef = useRef(1.0);
  const edlRadiusRef = useRef(1.0);

  const updatePointSize = useCallback((size) => {
    const tileset = tilesetRef.current;
    if (tileset?.customShader) {
      tileset.customShader.uniforms.u_pointSize.value = size;
    }
  }, [tilesetRef]);

  const updateEyeDomeLighting = useCallback((enabled) => {
    const tileset = tilesetRef.current;
    if (tileset) {
      tileset.pointCloudShading.eyeDomeLighting = enabled;
    }
  }, [tilesetRef]);

  const updateEyeDomeLightingStrength = useCallback((strength) => {
    edlStrengthRef.current = strength;
    const tileset = tilesetRef.current;
    if (tileset) {
      tileset.pointCloudShading.eyeDomeLightingStrength = strength;
    }
  }, [tilesetRef]);

  const updateEyeDomeLightingRadius = useCallback((radius) => {
    edlRadiusRef.current = radius;
    const tileset = tilesetRef.current;
    if (tileset) {
      tileset.pointCloudShading.eyeDomeLightingRadius = radius;
    }
  }, [tilesetRef]);

  const updateGlobeLighting = useCallback((enabled) => {
    const viewer = viewerRef.current;
    if (viewer) {
      viewer.scene.globe.enableLighting = enabled;
    }
  }, [viewerRef]);

  const updateAtmosphereRendering = useCallback((enabled) => {
    const viewer = viewerRef.current;
    if (viewer) {
      viewer.scene.skyAtmosphere.show = enabled;
    }
  }, [viewerRef]);

  const updateTerrainRendering = useCallback((enabled) => {
    const viewer = viewerRef.current;
    if (viewer) {
      viewer.scene.globe.show = enabled;
    }
  }, [viewerRef]);

  const updateUseBrowserRecommendationResolution = useCallback((enabled) => {
    const viewer = viewerRef.current;
    if (viewer) {
      viewer.useBrowserRecommendedResolution  = enabled;
    }
  }, [viewerRef]);

  const updateSSAO = useCallback((enabled, intensity) => {
    const viewer = viewerRef.current;
    if (!viewer?.scene?.postProcessStages?.ambientOcclusion) return;
    const ao = viewer.scene.postProcessStages.ambientOcclusion;
    ao.enabled = enabled;
    ao.uniforms.intensity = intensity;
  }, [viewerRef]);

  const updateFXAA = useCallback((enabled) => {
    const viewer = viewerRef.current;
    if (!viewer?.scene?.postProcessStages?.fxaa) return;
    viewer.scene.postProcessStages.fxaa.enabled = enabled;
  }, [viewerRef]);

  const updateContourLines = useCallback((enabled, spacing, thickLineInterval, baseThickness = 0.05) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    if (!enabled) {
      viewer.scene.globe.material = null;
      return;
    }

    try {
      const camera = viewer.camera;
      const ellipsoid = viewer.scene.globe.ellipsoid;
      const cameraHeight = ellipsoid.cartesianToCartographic(camera.position).height;

      let minHeight = 0;
      let maxHeight = Math.max(5000, cameraHeight * 2);

      const rectangle = viewer.camera.computeViewRectangle();
      if (rectangle) {
        minHeight = 0;
        maxHeight = 3500;
      }

      const layers = [];
      const heightRange = maxHeight - minHeight;
      const lineCount = Math.floor(heightRange / spacing);

      for (let i = 0; i <= lineCount; i++) {
        const height = minHeight + (i * spacing);
        const isThickLine = i % thickLineInterval === 0;

        const lineWidth = isThickLine ? baseThickness * 2 : baseThickness;
        const alpha = isThickLine ? 0.9 : 0.3;
        const lineColor = new Cesium.Color(1, 1, 1, alpha);

        layers.push({
          entries: [
            {
              height: height - 0.5 * lineWidth,
              color: lineColor
            },
            {
              height: height + 0.5 * lineWidth,
              color: lineColor
            },
          ],
        });
      }

      const material = Cesium.createElevationBandMaterial({
        scene: viewer.scene,
        layers: layers,
      });

      viewer.scene.globe.material = material;
    } catch (error) {
      console.error('Error creating contour lines:', error);
      viewer.scene.globe.material = null;
    }
  }, [viewerRef]);

  return {
    edlStrengthRef,
    edlRadiusRef,
    updatePointSize,
    updateEyeDomeLighting,
    updateEyeDomeLightingStrength,
    updateEyeDomeLightingRadius,
    updateGlobeLighting,
    updateAtmosphereRendering,
    updateTerrainRendering,
    updateUseBrowserRecommendationResolution,
    updateSSAO,
    updateFXAA,
    updateContourLines,
  };
};
